"use client";

import FlightSearchForm from "@/components/flightSearchSection/FlightSearchForm";
import CarSearchFormWrapper from "@/components/pages/car/searchForm";
import FerrySearchFormWrapper from "@/components/pages/ferry/searchForm";
import HotelSearchFormWrapper from "@/components/pages/hotel/searchForm";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { useRouter } from "@/i18n/navigation";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import BusSearchFormWrapper from "../../bus/searchForm";

const PRODUCT_ORDER = ["Flight", "Hotel", "Bus", "Car", "Ferry"];
const STATUS_KEYS = {
  Flight: "FlightStatus",
  Hotel: "HotelStatus",
  Bus: "BusStatus",
  Car: "CarStatus",
  Ferry: "FerryStatus",
};
const ICON_KEYS = {
  Flight: "new-flight",
  Hotel: "new-hotel",
  Bus: "new-bus",
  Car: "new-car",
  Ferry: "new-ferry",
};
const LABEL_KEYS = {
  Flight: "header.flight",
  Hotel: "header.hotel",
  Bus: "header.bus",
  Car: "header.car",
  Ferry: "header.ferry",
};

function CampaignDetailForms({ campaign }) {
  const t = useTranslationDetector();
  const router = useRouter();

  const visibleProductTypes = useMemo(() => {
    return PRODUCT_ORDER.filter(
      (productType) => campaign?.[STATUS_KEYS[productType]] === true,
    );
  }, [campaign]);

  const defaultProductType = visibleProductTypes.includes(campaign?.ProductType)
    ? campaign.ProductType
    : visibleProductTypes[0];

  const [activeProductType, setActiveProductType] =
    useState(defaultProductType);

  useEffect(() => {
    if (
      visibleProductTypes.length > 0 &&
      !visibleProductTypes.includes(activeProductType)
    ) {
      setActiveProductType(defaultProductType);
    }
  }, [visibleProductTypes, defaultProductType, activeProductType]);

  const FORM_COMPONENTS = useMemo(
    () => ({
      Flight: <FlightSearchForm scrollToForm={true} />,
      Bus: <BusSearchFormWrapper scrollToForm={true} />,
      Ferry: <FerrySearchFormWrapper scrollToForm={true} />,
      Hotel: (
        <HotelSearchFormWrapper
          hideLatestSearches
          hidePnrSection
          autoSelectFirstWhenClosed
          scrollToForm={true}
        />
      ),
      Car: (
        <CarSearchFormWrapper
          hideLatestSearches
          hidePnrSection
          scrollToForm={true}
        />
      ),
    }),
    [],
  );

  const formTitle = useMemo(() => {
    return {
      Flight: t("campaigns.campaignFlightFormTitle"),
      Bus: t("campaigns.campaignBusFormTitle"),
      Ferry: t("campaigns.campaignFerryFormTitle"),
      Hotel: t("campaigns.campaignHotelFormTitle"),
      Car: t("campaigns.campaignCarFormTitle"),
    };
  }, [t]);

  const showTabs = visibleProductTypes.length > 1;

  const handleTabClick = (productType) => {
    setActiveProductType(productType);
  };

  return (
    <div className="container mx-auto flex flex-col gap-4 py-[60px] md:gap-10">
      <span className="text-foreground text-[26px]/[34px] font-semibold">
        {formTitle[showTabs ? activeProductType : campaign?.ProductType]}
      </span>

      {showTabs && (
        <div className="mb-2 flex flex-wrap items-center justify-between md:mb-0 md:justify-start md:gap-8">
          {visibleProductTypes.map((productType) => {
            const isActive = activeProductType === productType;
            return (
              <button
                key={productType}
                type="button"
                onClick={() => handleTabClick(productType)}
                className={clsx(
                  "group flex flex-col items-center justify-between gap-1 text-center",
                  isActive && "text-primary",
                )}
              >
                <span
                  className={clsx(
                    "group-hover:bg-primary flex h-11 w-11 items-center justify-center rounded-lg p-2 transition-all duration-300 group-hover:-translate-y-1 group-hover:text-white md:h-14 md:w-14",
                    isActive && "bg-primary text-white",
                  )}
                >
                  <FontAwesomeIcon
                    icon={byPrefixAndName.fak[ICON_KEYS[productType]]}
                    className="text-[28px] md:text-4xl"
                  />
                </span>
                <span className="text-sm font-medium md:text-base">
                  {t(LABEL_KEYS[productType])}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {FORM_COMPONENTS[showTabs ? activeProductType : campaign?.ProductType]}
    </div>
  );
}

export default CampaignDetailForms;
